import _ from "lodash";
import React, { useRef, useEffect } from "react";
import classes from "./GlobalSearchBar.scss";
import UIModal from "UIComponents/UIModal";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import { withCombinedAccessBlocker } from "lib/PermissionAware";
import { withRouter } from "react-router";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import { useKey } from "react-use";
import SearchInput from "./SearchInput";
import Header from "./Header";
import RecentSearch from "./RecentSearch";
import SearchResultsComp from "./SearchResult";
import ModulesSection from "./ModulesSection";
import Suggestions from "./Suggestions";
import Footer from "./Footer";
import {
  typesConfig,
  getModulesFiltersList,
  getRecentSearchItems,
} from "./Utils";
import useLocalSuggestions from "./useLocalSuggestions";
import { navigate } from "modules/NavigationModule";

import { getUserCoursesQuery } from "../modules/GlobalSearchBarQueries";
import { getConfigurationQueryEnhancer } from "ChildrenPage/modules/ChildrenPageEnhancers";
import {
  getConfigurationQueryVariable,
  globalSearchConfigurationSelector,
} from "../modules/GlobalSearchBarModule";
import {
  updateGlobalSearchText,
  toggleVoiceSearch,
  toggleGlobalSearchBarModal,
  updateGlobalSearchModuleFilter,
  updateGlobalSearchCoursesContext,
} from "modules/Services";
import { recordEvent } from "lib/eventTracker";
import ACLStore from "lib/aclStore";
import { changeLocalSearchStorageData } from "services/localStorage";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

const loaderStyles = {
  containerStyle: {
    position: "absolute",
    zIndex: 2,
    height: "100%",
  },
  loadingContainerStyle: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
  },
};

/**
 * Here, pseudo interaction keys implies that
 * they don't have any functionality wired to them,
 * however when user presses these key, we don't want to
 * return focus back to search input.
 */
const PSEUDO_INTERACTION_KEYS = ["ArrowRight", "ArrowLeft"];
const INTERACTION_KEYS = ["ArrowUp", "ArrowDown", "Enter"];
const LOCAL_SEARCH_STORAGE_COUNT = 25;
const SEARCH_MODULE_NAME = "searches";

const GlobalSearchBar = props => {
  const {
    showSearchBar,
    toggleGlobalSearchBarModal,
    enableVoiceSearch,
    toggleVoiceSearch,
    userId,
    userInfo,
    navigate,
    searchText,
    module,
    updateGlobalSearchText,
    updateGlobalSearchCoursesContext,
    t,
    courses,
    shouldUpdateCourses,
    curriculumProgramType,
    orgId,
  } = props;

  const {
    localSuggestions,
    isLoading: isLocalSuggestionsLoading,
  } = useLocalSuggestions({
    t,
    module,
    searchText,
    orgId,
    courses,
    curriculumProgramType,
  });

  const searchInputRef = useRef();
  const modalContainerRef = useRef();

  const recentSearchResults = getRecentSearchItems({
    userId,
    module,
    userInfo,
    t,
  });
  const modulesList = getModulesFiltersList({ t, userInfo });

  useEffect(() => {
    if (shouldUpdateCourses) {
      updateGlobalSearchCoursesContext({ courses });
    }
  }, []);

  const setInputFocus = () => {
    searchInputRef.current?.focus?.();
  };

  const onSearchInputChange = (value = "") => {
    updateGlobalSearchText(value);

    recordEvent({
      eventName: "GLOBAL_SEARCH",
      eventData: {
        searchText: value,
        event_type: "SEARCH",
      },
    });
  };

  const onSelectModule = ({ type }) => {
    const { updateGlobalSearchModuleFilter } = props;

    updateGlobalSearchModuleFilter(type);

    setInputFocus();
  };

  const onUnselectModule = () => {
    const { updateGlobalSearchModuleFilter } = props;

    updateGlobalSearchModuleFilter(null);

    setInputFocus();
  };

  const onSuggestionClick = ({ item, e }) => {
    const type = item.type;
    // doing this as the item is being stringified in navigate and local storage and the subtitle can be a react element which causes the stringify to fail
    const subTitle = React.isValidElement(item.subTitle) ? "" : item.subTitle;
    const params = {
      ...item,
      type,
      subTitle,
      source: "GLOBAL_SEARCH",
    };
    let shouldNavigate = true;
    const routerParams = props.router.params;

    const isMouseClick = !!e.isTrusted;
    recordEvent({
      eventName: "GLOBAL_SEARCH",
      eventData: {
        clicked_item: type,
        searchText,
        filter_module: module,
        trigger: isMouseClick ? "MOUSE_CLICK" : "KEYBOARD",
      },
    });

    /**
     * @description unit plan details tab data doesn't render
     * when same unit plan is selected again.
     */
    if (
      routerParams?.course_id == item?.metadata?.courseId &&
      routerParams?.unit_id == item?.metadata?.unitPlanId &&
      item.type == typesConfig.UNIT_PLAN.id
    ) {
      shouldNavigate = false;
    }

    toggleGlobalSearchBarModal();
    //navigate to that particular module
    if (shouldNavigate) {
      navigate({ params });
    }
    //change in local storage data
    changeLocalSearchStorageData({
      data: params,
      userId,
      searchModuleName: SEARCH_MODULE_NAME,
      localSearchStorageCount: LOCAL_SEARCH_STORAGE_COUNT,
    });
  };

  const handleEnterKey = e => {
    e.stopPropagation();
    modalContainerRef.current?.querySelector(":focus")?.click();
    return;
  };

  const focusNextElement = (direction = "up") => {
    const focusableElements = [
      searchInputRef.current,
      ...(modalContainerRef.current?.querySelectorAll(
        "[data-elm=module-filter-item],[data-elm=search-item]"
      ) ?? []),
    ];
    const currentFocusedElement = modalContainerRef.current?.querySelector(
      ":focus"
    );
    const currentFocusedElementIndex = _.findIndex(
      focusableElements,
      currentFocusedElement
    );

    if (currentFocusedElementIndex === -1 || currentFocusedElement === null) {
      // focus search input if no valid element was already focused
      setInputFocus();
      return;
    }

    let nextFocusElementIndex;
    const lastIndex = focusableElements.length - 1;
    if (direction === "up") {
      nextFocusElementIndex =
        currentFocusedElementIndex === 0
          ? lastIndex
          : currentFocusedElementIndex - 1;
    } else if (direction === "down") {
      nextFocusElementIndex =
        currentFocusedElementIndex === lastIndex
          ? 0
          : currentFocusedElementIndex + 1;
    } else {
      console.error("invalid direction", direction);
      return;
    }

    focusableElements[nextFocusElementIndex].focus();
    setTimeout(() => {
      // select the texts in the element if the element is input element
      focusableElements[nextFocusElementIndex].select?.();
    }, 0);
  };

  const blurCurrentlyFocusedElement = () => {
    modalContainerRef.current
      ?.querySelector(":focus:not(#global-search-input)")
      ?.blur?.();
  };

  const setInitialFocus = () => {
    const firstSearchItemElement = modalContainerRef.current?.querySelector(
      "[data-elm=search-item]"
    );

    if (firstSearchItemElement) {
      firstSearchItemElement?.focus();
    } else {
      setInputFocus();
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setInitialFocus();
    }, 0);
  }, []);

  useEffect(() => {
    setInitialFocus();
  }, [module, recentSearchResults]);

  useKey("ArrowUp", () => focusNextElement("up"), {}, []);
  useKey("ArrowDown", () => focusNextElement("down"), {}, []);
  useKey("Enter", handleEnterKey, {}, []);
  useKey(
    event => {
      const { key } = event;
      return !_.includes(
        [...INTERACTION_KEYS, ...PSEUDO_INTERACTION_KEYS],
        key
      );
    },
    setInputFocus,
    {},
    []
  );

  if (!showSearchBar) {
    return null;
  }

  return (
    <UIModal
      isOpen={true}
      modalContent={classes.modalContent}
      closeOnOutSideClick={true}
      onRequestClose={toggleGlobalSearchBarModal}
    >
      <div
        ref={modalContainerRef}
        className={classes.container}
        onMouseMove={blurCurrentlyFocusedElement}
      >
        <Header />
        <SearchInput
          onChange={onSearchInputChange}
          module={module}
          onUnselectModule={onUnselectModule}
          enableVoiceSearch={enableVoiceSearch}
          toggleVoiceSearch={toggleVoiceSearch}
          ref={searchInputRef}
          setInputFocus={setInputFocus}
          searchText={searchText}
        />
        {/* <ModulesSection /> */}
        {/* <RecentSearch /> */}
        <div className={classes.focusZoneContainer}>
          {!searchText ? (
            !module ? (
              <React.Fragment>
                <ModulesSection
                  data={modulesList}
                  onSelectModule={onSelectModule}
                />

                <RecentSearch
                  data={recentSearchResults}
                  module={module}
                  onSuggestionClick={onSuggestionClick}
                />
              </React.Fragment>
            ) : _.size(recentSearchResults) ? (
              <RecentSearch
                data={recentSearchResults}
                module={module}
                onSuggestionClick={onSuggestionClick}
              />
            ) : (
              <div className={classes.suggestions}>
                <Suggestions
                  module={module}
                  onSuggestionClick={onSuggestionClick}
                />
              </div>
            )
          ) : null}

          {searchText ? (
            <div className={classes.searchResults}>
              <SearchResultsComp
                onSuggestionClick={onSuggestionClick}
                term={searchText}
                module={module}
                localSuggestions={localSuggestions}
                isLoading={isLocalSuggestionsLoading}
                isData={!isLocalSuggestionsLoading}
              />
            </div>
          ) : null}
        </div>
      </div>
      <Footer />
    </UIModal>
  );
};

const isGlobalSearchCourseExpired = updatedAt => {
  const tenMinuteMilliSeconds = 1000 * 60 * 30;
  const diff = Date.now() - updatedAt;
  return diff > tenMinuteMilliSeconds;
};

const getUserCurriculumCourses = ({ courses, curriculumIds = [] }) => {
  if (_.isEmpty(curriculumIds)) {
    return courses;
  }

  const filteredCourses = _.filter(courses, course => {
    const courseCurriculumId = _.get(course, "curriculumProgram.id", null);
    return _.includes(curriculumIds, courseCurriculumId);
  });

  return filteredCourses;
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const {
    id: userId,
    userEntityType,
    org_id: orgId,
    userCurriculumPrograms: curriculumPrograms,
  } = userInfo;
  const curriculumProgramType = _.uniq(_.map(curriculumPrograms, "type"));
  const targetCurriculumIds = _.map(curriculumPrograms, "id");

  const searchText = _.get(
    state,
    "app_services.globalSearchBar.searchText",
    ""
  );

  const showSearchBar = _.get(
    state,
    "app_services.globalSearchBar.showSearchBar",
    false
  );
  const enableVoiceSearch = _.get(
    state,
    "app_services.globalSearchBar.enableVoiceSearch",
    false
  );
  const globalSearchCourses = _.get(
    state,
    "app_services.globalSearchBar.courses.allCourses"
  );
  const globalSearchCourseUpdatedAt = _.get(
    state,
    "app_services.globalSearchBar.courses.updatedAt",
    null
  );

  const module = _.get(state, "app_services.globalSearchBar.module", null);

  const shouldUpdateCourses =
    globalSearchCourses === null ||
    globalSearchCourseUpdatedAt === null ||
    isGlobalSearchCourseExpired(globalSearchCourseUpdatedAt);
  const configurationQueryVariables = getConfigurationQueryVariable(state);

  const userCourses = getUserCurriculumCourses({
    courses: globalSearchCourses,
    curriculumIds: targetCurriculumIds,
  });

  return {
    accessPermissions: [
      "TeacherPortal:GlobalSearchBar",
      "StudentPortal:GlobalSearchBar",
    ],
    errorText: "Do not have permission to see this page",
    showErrorComp: false,
    orgId,
    userId,
    userEntityType,
    userInfo,
    searchText,
    showSearchBar,
    enableVoiceSearch,
    module,
    shouldUpdateCourses,
    configurationQueryVariables,
    courses: userCourses,
    curriculumProgramType,
    isData: true,
    isLoading: false,
    ...loaderStyles,
  };
};

const mapActionCreators = {
  navigate,
  updateGlobalSearchText,
  toggleVoiceSearch,
  toggleGlobalSearchBarModal,
  updateGlobalSearchModuleFilter,
  updateGlobalSearchCoursesContext,
};

export default compose(
  withRouter,
  I18nHOC({
    resource: [
      "common",
      "teacherHomePage",
      "classRoom",
      "configuration",
      "attendance",
      "integration",
    ],
  }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getUserCoursesQuery, {
    name: "getGlobalSearchUserCourses",
    skip: ({ accessPermissions, shouldUpdateCourses }) => {
      return !ACLStore.can(accessPermissions) || !shouldUpdateCourses;
    },
    options: ({ userId, userEntityType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
      },
    }),
    props({ getGlobalSearchUserCourses, ownProps: { isData, isLoading } }) {
      const queryResult = _.get(getGlobalSearchUserCourses, "node", {});
      const courses = _.get(getGlobalSearchUserCourses, "node.courses", []);

      return {
        courses,
        isData: !_.isEmpty(queryResult) && isData,
        isLoading:
          getGlobalSearchUserCourses["networkStatus"] == 1 ||
          getGlobalSearchUserCourses["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  getConfigurationQueryEnhancer(globalSearchConfigurationSelector, {
    fetchPolicy: "cache-and-network",
  }),
  withCombinedAccessBlocker,
  withLoader
)(GlobalSearchBar);
